import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, 
  Download, 
  Share2, 
  Award,
  Sparkles,
  Check,
  Clock
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";

/**
 * Achievement Badge Component
 * Displays shareable milestone badges when users reach meditation goals
 */

// Milestone definitions with beautiful descriptions
const MILESTONES = {
  100: {
    name: "First Steps",
    subtitle: "100 Minutes of Practice",
    description: "You've taken the first steps on your journey inward.",
    color: "from-emerald-400 to-teal-500",
    bgColor: "from-emerald-500/20 to-teal-500/20",
    icon: "🌱"
  },
  500: {
    name: "Finding Flow",
    subtitle: "500 Minutes of Practice", 
    description: "Your practice is becoming a beautiful habit.",
    color: "from-violet-400 to-purple-500",
    bgColor: "from-violet-500/20 to-purple-500/20",
    icon: "🌿"
  },
  1000: {
    name: "Dedicated Practice",
    subtitle: "1,000 Minutes of Practice",
    description: "Over 16 hours of presence. You're truly committed.",
    color: "from-amber-400 to-orange-500",
    bgColor: "from-amber-500/20 to-orange-500/20",
    icon: "🌳"
  },
  5000: {
    name: "Deep Transformation",
    subtitle: "5,000 Minutes of Practice",
    description: "83+ hours of inner work. You've transformed.",
    color: "from-rose-400 to-pink-500",
    bgColor: "from-rose-500/20 to-pink-500/20",
    icon: "🏔️"
  },
  10000: {
    name: "Mastery Path",
    subtitle: "10,000 Minutes of Practice",
    description: "166+ hours. A true master of presence.",
    color: "from-yellow-300 to-amber-400",
    bgColor: "from-yellow-400/20 to-amber-400/20",
    icon: "☀️"
  }
};

// The shareable badge card component
const BadgeCard = ({ milestone, totalMinutes, userName, forExport = false }) => {
  const data = MILESTONES[milestone];
  if (!data) return null;

  const formattedDate = new Date().toLocaleDateString("en-US", { 
    month: "long", 
    day: "numeric", 
    year: "numeric" 
  });

  return (
    <div 
      className={`relative overflow-hidden ${forExport ? "w-[1080px] h-[1080px]" : "w-full aspect-square max-w-[320px]"}`}
      style={{ 
        background: "linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)"
      }}
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br ${data.bgColor} rounded-full blur-3xl opacity-60`} />
        <div className={`absolute bottom-1/4 right-1/4 w-48 h-48 bg-gradient-to-br ${data.bgColor} rounded-full blur-2xl opacity-40`} />
      </div>

      {/* Content */}
      <div className={`relative h-full flex flex-col items-center justify-center ${forExport ? "p-20" : "p-8"}`}>
        {/* RECLAIM branding */}
        <p className={`text-white/40 tracking-[0.3em] uppercase ${forExport ? "text-2xl mb-12" : "text-xs mb-6"}`}>
          RECLAIM
        </p>

        {/* Badge icon */}
        <div className={`relative mb-6 ${forExport ? "mb-12" : ""}`}>
          <div className={`${forExport ? "w-40 h-40" : "w-24 h-24"} rounded-full bg-gradient-to-br ${data.bgColor} flex items-center justify-center`}>
            <span className={forExport ? "text-7xl" : "text-4xl"}>{data.icon}</span>
          </div>
          {/* Glow ring */}
          <div className={`absolute inset-0 ${forExport ? "w-40 h-40" : "w-24 h-24"} rounded-full bg-gradient-to-br ${data.color} opacity-20 blur-xl`} />
        </div>

        {/* Achievement text */}
        <h2 className={`font-heading text-white text-center ${forExport ? "text-5xl mb-4" : "text-2xl mb-2"}`}>
          {data.name}
        </h2>
        <p className={`text-white/60 text-center ${forExport ? "text-2xl mb-8" : "text-sm mb-4"}`}>
          {data.subtitle}
        </p>

        {/* Description */}
        <p className={`text-white/40 text-center max-w-xs ${forExport ? "text-xl max-w-lg mb-12" : "text-xs mb-6"}`}>
          {data.description}
        </p>

        {/* Stats */}
        <div className={`flex items-center gap-2 ${forExport ? "text-xl" : "text-sm"}`}>
          <Clock className={`text-white/40 ${forExport ? "w-6 h-6" : "w-4 h-4"}`} />
          <span className="text-white/60">{Math.round(totalMinutes).toLocaleString()} total minutes</span>
        </div>

        {/* Date achieved */}
        <p className={`text-white/30 mt-4 ${forExport ? "text-lg mt-8" : "text-xs"}`}>
          Achieved {formattedDate}
        </p>

        {/* Footer */}
        <p className={`absolute bottom-6 text-white/20 ${forExport ? "text-lg bottom-12" : "text-[10px]"}`}>
          reclaim.app
        </p>
      </div>
    </div>
  );
};

// Main Achievement Modal Component
export const AchievementModal = ({ 
  isOpen, 
  onClose, 
  milestone, 
  totalMinutes,
  userName = "You",
  isNewAchievement = false 
}) => {
  const cardRef = useRef(null);
  const exportRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const handleDownload = async () => {
    if (!exportRef.current) return;
    
    setIsDownloading(true);
    try {
      const canvas = await html2canvas(exportRef.current, {
        scale: 1,
        backgroundColor: null,
        useCORS: true
      });
      
      const link = document.createElement("a");
      link.download = `reclaim-${MILESTONES[milestone]?.name.toLowerCase().replace(/\s/g, "-")}-badge.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      
      toast.success("Badge saved to your device");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download badge");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    if (!exportRef.current) return;
    
    setIsSharing(true);
    try {
      const canvas = await html2canvas(exportRef.current, {
        scale: 1,
        backgroundColor: null,
        useCORS: true
      });
      
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error("Failed to create image");
          setIsSharing(false);
          return;
        }

        const file = new File([blob], "reclaim-achievement.png", { type: "image/png" });
        
        if (navigator.share && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: `I reached ${MILESTONES[milestone]?.name} on Reclaim!`,
              text: `${MILESTONES[milestone]?.subtitle} - ${MILESTONES[milestone]?.description}`
            });
            toast.success("Shared successfully!");
          } catch (shareErr) {
            if (shareErr.name !== "AbortError") {
              // Fallback to download if share fails
              handleDownload();
            }
          }
        } else {
          // Fallback for browsers without share API
          handleDownload();
        }
        setIsSharing(false);
      }, "image/png");
    } catch (err) {
      console.error("Share error:", err);
      toast.error("Failed to share badge");
      setIsSharing(false);
    }
  };

  if (!isOpen || !milestone || !MILESTONES[milestone]) return null;

  const data = MILESTONES[milestone];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-[#1a1a2e] rounded-3xl max-w-sm w-full overflow-hidden"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={20} className="text-white/60" />
          </button>

          {/* New achievement celebration */}
          {isNewAchievement && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full"
            >
              <Sparkles size={14} className="text-amber-400" />
              <span className="text-amber-300 text-xs font-medium">New Achievement!</span>
            </motion.div>
          )}

          {/* Badge preview */}
          <div ref={cardRef} className="p-6 pt-14">
            <BadgeCard 
              milestone={milestone} 
              totalMinutes={totalMinutes}
              userName={userName}
            />
          </div>

          {/* Action buttons */}
          <div className="p-6 pt-0 flex gap-3">
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-white/10 hover:bg-white/15 rounded-xl text-white/80 transition-colors"
            >
              <Download size={18} />
              <span className="text-sm">{isDownloading ? "Saving..." : "Save"}</span>
            </button>
            <button
              onClick={handleShare}
              disabled={isSharing}
              className={`flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r ${data.color} rounded-xl text-white font-medium transition-all hover:opacity-90`}
            >
              <Share2 size={18} />
              <span className="text-sm">{isSharing ? "Sharing..." : "Share"}</span>
            </button>
          </div>
        </motion.div>

        {/* Hidden export canvas (1080x1080 for Instagram) */}
        <div className="fixed -left-[9999px] -top-[9999px]">
          <div ref={exportRef}>
            <BadgeCard 
              milestone={milestone} 
              totalMinutes={totalMinutes}
              userName={userName}
              forExport={true}
            />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Achievement Badge Display (for achievements list)
export const AchievementBadge = ({ 
  milestone, 
  achieved = false, 
  totalMinutes = 0,
  onClick 
}) => {
  const data = MILESTONES[milestone];
  if (!data) return null;

  return (
    <motion.button
      whileHover={achieved ? { scale: 1.02 } : {}}
      whileTap={achieved ? { scale: 0.98 } : {}}
      onClick={achieved ? onClick : undefined}
      disabled={!achieved}
      className={`relative w-full p-4 rounded-2xl border transition-all ${
        achieved 
          ? `bg-gradient-to-br ${data.bgColor} border-white/20 cursor-pointer hover:border-white/30`
          : "bg-white/5 border-white/10 opacity-50 cursor-default"
      }`}
    >
      <div className="flex items-center gap-4">
        {/* Icon */}
        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
          achieved ? `bg-gradient-to-br ${data.bgColor}` : "bg-white/10"
        }`}>
          {achieved ? (
            <span className="text-2xl">{data.icon}</span>
          ) : (
            <Award size={24} className="text-white/30" />
          )}
        </div>

        {/* Text */}
        <div className="flex-1 text-left">
          <h3 className={`font-medium ${achieved ? "text-white" : "text-white/40"}`}>
            {data.name}
          </h3>
          <p className={`text-sm ${achieved ? "text-white/60" : "text-white/30"}`}>
            {data.subtitle}
          </p>
        </div>

        {/* Status */}
        {achieved ? (
          <div className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-full">
            <Check size={14} className="text-emerald-400" />
            <span className="text-emerald-300 text-xs">Earned</span>
          </div>
        ) : (
          <div className="text-white/30 text-xs">
            {milestone - totalMinutes}m to go
          </div>
        )}
      </div>
    </motion.button>
  );
};

export default AchievementModal;
